<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6">
            <v-card-title>
              <h3 class="font-weight-bold">Create Chat Message</h3>
            </v-card-title>
            <v-card-text>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                  <!-- Chat Selection -->
                  <v-col md="6">
                    <v-autocomplete
                      v-model="form.chat_id"
                      :items="chats"
                      item-text="name"
                      item-value="id"
                      label="Select Chat *"
                      :rules="chatRules"
                      prepend-inner-icon="mdi-message"
                      :error-messages="getError('chat_id')"
                      required
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ item.name || 'Direct Chat' }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ item.is_group ? 'Group Chat' : 'Direct Chat' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                      <template v-slot:selection="{ item }">
                        {{ item.name || 'Direct Chat' }}
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <!-- Active Status -->
                  <v-col md="6">
                    <v-switch
                      v-model="form.is_active"
                      :label="`Message Status: ${form.is_active ? 'Active' : 'Inactive'}`"
                      color="success"
                      inset
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row>
                  <!-- Message Content -->
                  <v-col md="12">
                    <v-textarea
                      v-model="form.message"
                      label="Message Content"
                      :rules="messageRules"
                      :error-messages="getError('message')"
                      prepend-inner-icon="mdi-message-text"
                      outlined
                      rows="4"
                      counter="1000"
                      hint="Enter your message content here"
                    ></v-textarea>
                  </v-col>
                </v-row>

                <v-row>
                  <!-- File Upload -->
                  <v-col md="12">
                    <v-file-input
                      v-model="form.file"
                      label="Attach File"
                      :rules="fileRules"
                      :error-messages="getError('file')"
                      prepend-icon="mdi-paperclip"
                      outlined
                      show-size
                      accept="*/*"
                      hint="Maximum file size: 10MB"
                    >
                      <template v-slot:selection="{ text }">
                        <v-chip
                          color="deep-purple accent-4"
                          dark
                          label
                          small
                        >
                          {{ text }}
                        </v-chip>
                      </template>
                    </v-file-input>
                  </v-col>
                </v-row>

                <!-- Message Preview -->
                <v-row v-if="form.message || form.file">
                  <v-col md="12">
                    <v-card outlined class="preview-card">
                      <v-card-subtitle>
                        <v-icon left>mdi-eye</v-icon>
                        Message Preview
                      </v-card-subtitle>
                      <v-card-text>
                        <div v-if="form.message" class="message-preview">
                          <strong>Message:</strong>
                          <div class="mt-2">{{ form.message }}</div>
                        </div>
                        <div v-if="form.file" class="file-preview mt-3">
                          <strong>Attached File:</strong>
                          <v-chip
                            color="blue"
                            dark
                            small
                            class="ml-2"
                          >
                            <v-icon left small>mdi-file</v-icon>
                            {{ form.file.name }}
                          </v-chip>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Action Buttons -->
                <v-row class="mt-4">
                  <v-col md="12" class="text-right">
                    <v-btn
                      color="grey"
                      outlined
                      @click="$router.go(-1)"
                      class="mr-2"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      @click="submitForm"
                      :loading="isSubmitting"
                      :disabled="!isFormValid"
                    >
                      <v-icon left>mdi-send</v-icon>
                      Send Message
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  data() {
    return {
      valid: false,
      isSubmitting: false,
      chats: [],
      form: {
        chat_id: null,
        message: "",
        file: null,
        is_active: true,
      },
      chatRules: [
        (v) => !!v || "Chat selection is required",
      ],
      messageRules: [
        (v) => {
          if (!v && !this.form.file) {
            return "Either message content or file attachment is required";
          }
          if (v && v.length > 1000) {
            return "Message cannot exceed 1000 characters";
          }
          return true;
        },
      ],
      fileRules: [
        (v) => {
          if (!v && !this.form.message) {
            return "Either message content or file attachment is required";
          }
          if (v && v.size > 10 * 1024 * 1024) {
            return "File size cannot exceed 10MB";
          }
          return true;
        },
      ],
      errors: {},
    };
  },
  computed: {
    isFormValid() {
      return (
        this.form.chat_id &&
        (this.form.message || this.form.file) &&
        this.valid
      );
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  mounted() {
    this.getChats();
    // If chat_id is passed as query parameter, select it
    if (this.$route.query.chat_id) {
      this.form.chat_id = parseInt(this.$route.query.chat_id);
    }
  },
  methods: {
    async getChats() {
      try {
        const response = await axios.get("chats");
        this.chats = response.data.data;
      } catch (error) {
        console.error("Error fetching chats:", error);
        this.$store.dispatch("errors/setError", "Failed to fetch chats");
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSubmitting = true;
      this.errors = {};

      try {
        const formData = new FormData();
        
        formData.append("chat_id", this.form.chat_id);
        formData.append("sender_id", this.user.id);
        if (this.form.message) {
          formData.append("message", this.form.message);
        }
        if (this.form.file) {
          formData.append("file", this.form.file);
        }
        formData.append("is_active", this.form.is_active ? 1 : 0);

        await axios.post("chat_messages", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$store.dispatch("errors/setSuccess", "Chat message sent successfully!");
        this.$router.push("/chat-messages");
      } catch (error) {
        console.error("Error creating chat message:", error);
        
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$store.dispatch("errors/setError", 
            error.response?.data?.message || "Failed to send chat message");
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    getError(field) {
      return this.errors[field] ? this.errors[field][0] : "";
    },

    resetForm() {
      this.form = {
        chat_id: null,
        message: "",
        file: null,
        is_active: true,
      };
      this.errors = {};
      this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped>
.preview-card {
  background-color: #f8f9fa;
}

.message-preview {
  white-space: pre-wrap;
  word-break: break-word;
}

.file-preview {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}
</style>